import React, { useState } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { createJobCode } from '../../../../core/API/scheduler';
import styles from '../SchedulerSetting.module.scss';

interface CreateJobCodeProps {
	backButtonClick: () => void;
	onJobCodeCreated?: () => void;
	defaultJobNumber?: number;
}

const CreateJobCode: React.FC<CreateJobCodeProps> = ({ backButtonClick, onJobCodeCreated, defaultJobNumber }) => {
	const [jobCode, setJobCode] = useState({ jobNumber: String(typeof defaultJobNumber === 'number' ? defaultJobNumber : ''), name: '', color: '#446388' });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!jobCode.jobNumber.trim()) {
			setError('Job code number is required');
			return;
		}

		if (!jobCode.name.trim()) {
			setError('Job code name is required');
			return;
		}

		// Validate job code number is numeric and within 2 digits
		const jobNum = parseInt(jobCode.jobNumber, 10);
		if (isNaN(jobNum) || jobNum < 0 || jobNum > 99) {
			setError('Job code number must be between 0 and 99');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			await createJobCode({
				jobNumber: parseInt(jobCode.jobNumber, 10),
				label: jobCode.name,
				backgroundColor: jobCode.color,
				textColor: '#000000',
			} as any);

			onJobCodeCreated?.();

			// Navigate back after a short delay
			setTimeout(() => {
				backButtonClick();
			}, 1500);
		} catch (err: any) {
			const errorMessage = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to create job code';
			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.createJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Create New Job Code</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Job Code Number"
					name="jobNumber"
					type="number"
					placeholder="0-99"
					value={jobCode.jobNumber}
					onChange={(e) => {
						const value = e.target.value;
						// Restrict to maximum 2 digits
						if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) <= 99)) {
							setJobCode({ ...jobCode, jobNumber: value });
						}
					}}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0, max: 99, maxLength: 2 }}
				/>

				<TextField
					label="Job Code Name"
					name="name"
					placeholder="Enter job code name"
					value={jobCode.name}
					onChange={(e) => setJobCode({ ...jobCode, name: e.target.value })}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				<div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
					<label className={styles.label} htmlFor="color-picker">
						Color
					</label>
					<input
						id="color-picker"
						type="color"
						value={jobCode.color}
						onChange={(e) => setJobCode({ ...jobCode, color: e.target.value })}
						style={{
							width: 100,
							height: 40,
							borderRadius: 4,
							border: '1px solid #ccc',
							cursor: 'pointer',
						}}
					/>
				</div>
			</form>
		</div>
	);
};

export default CreateJobCode;
